import { defineMessages } from 'react-intl';

/**
 * react-intl message descriptors for dictation bubble UI, shared dictation
 * surfaces, and default dictation settings. Some entries reuse stable ids that
 * match other dictation or shared UI strings.
 */
export const dictationIntlMessages = defineMessages({
  stillDictatingTitle: {
    id: 'I7pD5EUeUh',
    defaultMessage: 'Still dictating?',
    description: 'Title when dictation is still active after speech ends',
  },
  stillDictatingSubtitle: {
    id: 'G1t2k018kg',
    // U+2019 RIGHT SINGLE QUOTATION MARK — typographic apostrophe (contractions)
    defaultMessage: 'I’ll stop after 10 seconds of silence',
    description: 'Explains auto-stop countdown after silence while still dictating',
  },
  silenceWarningTitle: {
    id: '6HQlEdbbVs',
    defaultMessage: 'I can’t hear you!',
    description: 'Title when the dictation bubble detects silence before any transcript',
  },
  silenceWarningSubtitle: {
    id: 'YdY7DxVKdb',
    defaultMessage: 'Check your microphone',
    description: 'Subtitle prompting microphone check when silence is detected',
  },
  handsFreeSubtitle: {
    id: 'kBAArsvH/K',
    defaultMessage: 'Hands-free',
    description:
      'Subtitle shown while dictating in hands-free mode in the floating widget',
  },
  endHandsFree: {
    id: 'xUYglrDHZ7',
    defaultMessage: 'End hands-free',
    description:
      'Accessibility label for the button that stops hands-free dictation in the floating widget',
  },
  maxDurationApproachingTitle: {
    id: 'h20jqPL3JM',
    defaultMessage: 'Max duration approaching...',
    description: 'Title when dictation is nearing the maximum allowed duration',
  },
  maxDurationApproachingSubtitle: {
    id: 'zSqPWs4mLz',
    defaultMessage: 'You can dictate for up to 10 mins',
    description: 'Subtitle explaining maximum dictation duration',
  },
  stillProcessing: {
    id: 'gqKwiQusDc',
    defaultMessage: 'Still processing...',
    description: 'Caption shown while dictation audio is still being processed',
  },
  dictating: {
    id: 'Uoxp3bEn6c',
    defaultMessage: 'Dictating',
    description: 'Primary label while dictation is actively listening',
  },
  cancelButton: {
    id: '0GT0SIETlE',
    defaultMessage: 'Cancel',
    description: 'Cancel button label',
  },
  errorMicPermissionDenied: {
    id: '2NPU7tXyNZ',
    defaultMessage: 'Please enable mic permissions',
    description: 'Dictation error when microphone permission is denied',
  },
  errorNoMicDevices: {
    id: '4aBOdxHxu0',
    defaultMessage: 'No microphone found',
    description: 'Dictation error when no microphone device is available',
  },
  errorFailedToGetAudioStream: {
    id: 'nuzTJdJv9e',
    defaultMessage: 'Failed to get audio stream',
    description: 'Dictation error when the audio stream cannot be opened',
  },
  errorPressAndHold: {
    id: 'B+mLUkXmMt',
    defaultMessage: 'Hold or double press to dictate',
    description: 'Dictation hint when press-and-hold mode is required',
  },
  errorNoAudioDetected: {
    id: 'oa6G+Fdzia',
    defaultMessage: 'No audio detected',
    description: 'Dictation error when no audio is detected from the microphone',
  },
  errorNoWordsDetected: {
    id: 'efU9LcJHkq',
    defaultMessage: 'No words detected',
    description: 'Dictation error when speech contained no recognizable words',
  },
  errorCancelled: {
    id: 'JYqW9xBwlO',
    defaultMessage: 'Cancelled',
    description: 'Dictation stopped by user or cancelled',
  },
  errorCouldNotConnectToHeidi: {
    id: 'LeNtEciP0U',
    defaultMessage: 'Could not connect to Heidi',
    description: 'Dictation error when the client cannot reach Heidi services',
  },
  errorAuthenticationRequired: {
    id: 'iAav8c6Thl',
    defaultMessage: 'Sign in to use Dictate',
    description: 'Dictation error when the user authentication is missing or expired',
  },
  errorChunkRecorderError: {
    id: '3jFLXHdLfP',
    defaultMessage: 'Chunk recorder error',
    description: 'Dictation error from the chunk recording pipeline',
  },
  errorNoInputFieldSelected: {
    id: 'NBx1QKxkt/',
    defaultMessage: 'No input field selected',
    description: 'Dictation error when no text field is focused for insertion',
  },
  errorSomethingWentWrong: {
    id: 'OfHcwA8WqC',
    defaultMessage: 'Something went wrong',
    description: 'Generic dictation error fallback',
  },
  helpLink: {
    id: 'k20V7WKFFZ',
    defaultMessage: 'Help',
    description: 'Link label to dictation / microphone troubleshooting help',
  },
  preferencesDefaultDictation: {
    id: 'hHjnPafTZY',
    defaultMessage: 'Default dictation',
    description: 'Settings label for default dictation cleanup mode',
  },
  preferencesDictationMethod: {
    id: '4/u9eFYV3y',
    defaultMessage: 'Select which method you want to use when dictating content',
    description: 'Supporting text under default dictation preference',
  },
  preferencesSelectDictationMode: {
    id: 'FLdIezclkH',
    defaultMessage: 'Select a default dictation mode',
    description: 'Placeholder on default dictation mode select when empty',
  },
  dictationModeSmart: {
    id: 'd81JRuxicK',
    defaultMessage: 'Smart dictation',
    description: 'Label for smart dictation mode',
  },
  dictationModeWordForWord: {
    id: 'yFb+3TKgYZ',
    defaultMessage: 'Word-for-word',
    description: 'Label for word-for-word dictation mode',
  },
  usingSystemDefaultMicrophone: {
    id: 'H2NGpoKCGs',
    defaultMessage: 'Using system default microphone.',
    description:
      'LISTENING subtitle when Dictate fell back from a missing preferred mic to the OS default',
  },
  usingSessionMicrophone: {
    id: 'y0mXhGzDT7',
    defaultMessage: 'Using session microphone.',
    description:
      'LISTENING subtitle when Dictate fell back from a missing preferred mic to the session recording mic',
  },
});
